import React, { useRef, useMemo, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import { Stars } from "@react-three/drei";
import * as THREE from "three";
import MoonBody from "./MoonBody";

// ---------------------------------------------------------------------------
// City grid geometry
// ---------------------------------------------------------------------------
const GRID = 5;                 // blocks per side
const BLOCK = 20;               // block footprint
const ROAD = 8;                 // road width
const STEP = BLOCK + ROAD;
const SPAN = GRID * BLOCK + (GRID + 1) * ROAD;
const HALF = SPAN / 2;
const NPC_COUNT = 500;

const _dummy = new THREE.Object3D();
const _calm = new THREE.Color(0.22, 0.74, 0.97);
const _hot = new THREE.Color(1.0, 0.34, 0.13);
const _tmpCol = new THREE.Color();

// deterministic pseudo-random
function mulberry(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// window / facade texture, cached per type
function makeWindowTexture(cols, rows, lit, tint, seedn) {
  const c = document.createElement("canvas");
  c.width = 128; c.height = 256;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#0a0d16"; ctx.fillRect(0, 0, 128, 256);
  const rnd = mulberry(seedn);
  const pw = 128 / cols, ph = 256 / rows;
  for (let x = 0; x < cols; x++) {
    for (let y = 0; y < rows; y++) {
      const on = rnd() < lit;
      ctx.fillStyle = on ? tint : "#0c1019";
      ctx.fillRect(x * pw + 1, y * ph + 1, pw - 2, ph - 2);
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function makeRoadTexture() {
  const c = document.createElement("canvas");
  c.width = 64; c.height = 128;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#1c1f27"; ctx.fillRect(0, 0, 64, 128);
  // curb edges
  ctx.fillStyle = "#2b2f39"; ctx.fillRect(0, 0, 4, 128); ctx.fillRect(60, 0, 4, 128);
  // dashed centre line
  ctx.fillStyle = "#e7c15a";
  for (let y = 8; y < 128; y += 34) ctx.fillRect(30, y, 4, 18);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

// road-centre coordinates and block-centre coordinates
const roadCenters = [];
for (let k = 0; k <= GRID; k++) roadCenters.push(-HALF + k * STEP + ROAD / 2);
const blockCenters = [];
for (let k = 0; k < GRID; k++) blockCenters.push(-HALF + k * STEP + ROAD + BLOCK / 2);

// ---------------------------------------------------------------------------
// Buildings — low-poly boxes; textures cached per archetype and reused
// ---------------------------------------------------------------------------
function useBuildings() {
  return useMemo(() => {
    const rnd = mulberry(90210);
    const tex = {
      tower: makeWindowTexture(6, 20, 0.34, "#a9d4ff", 11),
      apartment: makeWindowTexture(5, 12, 0.42, "#ffd8a0", 22),
      store: makeWindowTexture(6, 4, 0.7, "#bfe9ff", 33),
      house: makeWindowTexture(3, 3, 0.5, "#ffcf8f", 44),
      hospital: makeWindowTexture(6, 10, 0.6, "#dff3ff", 55),
    };
    const baseColor = {
      tower: "#151b28", apartment: "#20202b", store: "#1a2230", house: "#241f22", hospital: "#1c2a33",
    };
    const signColors = ["#ff5a7a", "#5ad1ff", "#ffd23f", "#7cff9e", "#c07bff"];
    const items = [];
    const cx = (GRID - 1) / 2;
    const hospitalBlock = [3, 1]; // world (x≈28, z≈-28) — inside the default ground camera frame

    for (let bi = 0; bi < GRID; bi++) {
      for (let bj = 0; bj < GRID; bj++) {
        const dist = Math.hypot(bi - cx, bj - cx);
        const gx = blockCenters[bi];
        const gz = blockCenters[bj];
        const isHospital = bi === hospitalBlock[0] && bj === hospitalBlock[1];
        // sub-plots inside the block (2x2)
        const plots = [[-1, -1], [1, -1], [-1, 1], [1, 1]];
        plots.forEach((pl, pi) => {
          if (!isHospital && rnd() < 0.12) return; // occasional empty lot / plaza
          const px = gx + pl[0] * BLOCK * 0.24 + (rnd() - 0.5) * 1.2;
          const pz = gz + pl[1] * BLOCK * 0.24 + (rnd() - 0.5) * 1.2;
          let type;
          if (isHospital && pi === 0) type = "hospital";
          else if (dist < 1.2) type = rnd() > 0.25 ? "tower" : "apartment";
          else if (dist < 2.4) type = rnd() > 0.45 ? "apartment" : (rnd() > 0.5 ? "store" : "tower");
          else type = rnd() > 0.55 ? "house" : "store";

          let w, h, d;
          if (type === "tower") { w = 3 + rnd() * 2.2; d = 3 + rnd() * 2.2; h = 14 + rnd() * 22; }
          else if (type === "apartment") { w = 4 + rnd() * 2; d = 4 + rnd() * 2; h = 8 + rnd() * 6; }
          else if (type === "store") { w = 4 + rnd() * 2.5; d = 4 + rnd() * 2.5; h = 2.4 + rnd() * 1.6; }
          else if (type === "hospital") { w = 7; d = 7; h = 16; }
          else { w = 2.6 + rnd() * 1.6; d = 2.6 + rnd() * 1.6; h = 2.2 + rnd() * 1.4; }

          items.push({
            key: `${bi}-${bj}-${pi}`,
            position: [px, 0, pz],
            size: [w, h, d],
            type,
            color: baseColor[type],
            tex: tex[type],
            texRepeat: [Math.max(1, Math.round(w / 2.2)), Math.max(1, Math.round(h / 3))],
            sign: type === "store" ? signColors[Math.floor(rnd() * signColors.length)] : null,
          });
        });
      }
    }
    return items;
  }, []);
}

// Buildings instanced per archetype -> a handful of draw calls instead of ~100
function InstancedBuildings({ items, color, tex, repeat, winIntensity }) {
  const ref = useRef();
  const sharedTex = useMemo(() => {
    const t = tex.clone();
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(repeat[0], repeat[1]);
    t.needsUpdate = true;
    return t;
  }, [tex, repeat]);
  useEffect(() => {
    if (!ref.current) return;
    items.forEach((it, i) => {
      const [w, h, d] = it.size;
      _dummy.position.set(it.position[0], h / 2, it.position[2]);
      _dummy.rotation.set(0, 0, 0);
      _dummy.scale.set(w, h, d);
      _dummy.updateMatrix();
      ref.current.setMatrixAt(i, _dummy.matrix);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  }, [items]);
  if (!items.length) return null;
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, items.length]} frustumCulled={false}>
      <boxGeometry args={[1, 1, 1]} />
      <meshLambertMaterial
        color={color}
        map={sharedTex}
        emissive={"#ffffff"}
        emissiveMap={sharedTex}
        emissiveIntensity={winIntensity}
      />
    </instancedMesh>
  );
}

function Hospital({ item, winIntensity }) {
  const [w, h, d] = item.size;
  const tex = useMemo(() => {
    const t = item.tex.clone();
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(3, 5);
    t.needsUpdate = true;
    return t;
  }, [item]);
  return (
    <group position={item.position}>
      <mesh position={[0, h / 2, 0]}>
        <boxGeometry args={[w, h, d]} />
        <meshLambertMaterial color={item.color} map={tex} emissive={"#ffffff"} emissiveMap={tex} emissiveIntensity={Math.max(0.5, winIntensity)} />
      </mesh>
      <group position={[0, h * 0.8, d / 2 + 0.06]}>
        <mesh><boxGeometry args={[1.1, 0.32, 0.08]} /><meshBasicMaterial color={"#ff2e2e"} toneMapped={false} /></mesh>
        <mesh><boxGeometry args={[0.32, 1.1, 0.08]} /><meshBasicMaterial color={"#ff2e2e"} toneMapped={false} /></mesh>
      </group>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Roads + sidewalks
// ---------------------------------------------------------------------------
function Roads({ roadTex }) {
  const tex = useMemo(() => {
    const t = roadTex.clone();
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(1, SPAN / 12);
    t.needsUpdate = true;
    return t;
  }, [roadTex]);
  return (
    <group>
      {roadCenters.map((rc, i) => (
        <mesh key={`v${i}`} position={[rc, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[ROAD, SPAN]} />
          <meshBasicMaterial map={tex} />
        </mesh>
      ))}
      {roadCenters.map((rc, i) => (
        <group key={`h${i}`} position={[0, 0.021, rc]} rotation={[0, Math.PI / 2, 0]}>
          <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
            <planeGeometry args={[ROAD, SPAN]} />
            <meshBasicMaterial map={tex} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// ---------------------------------------------------------------------------
// Instanced NPC crowd — 500 pedestrians walking the sidewalks
// ---------------------------------------------------------------------------
function Crowd({ simRef }) {
  const bodyRef = useRef();
  const headRef = useRef();
  const haloRef = useRef();
  const haloMat = useRef();

  const npcs = useMemo(() => {
    const rnd = mulberry(1337);
    const arr = [];
    const laneOff = ROAD / 2 + 0.9; // sidewalk just outside the carriageway
    for (let i = 0; i < NPC_COUNT; i++) {
      const axis = rnd() > 0.5 ? 0 : 1;            // 0 = walks along X, 1 = along Z
      const rc = roadCenters[Math.floor(rnd() * roadCenters.length)];
      const side = rnd() > 0.5 ? 1 : -1;
      arr.push({
        axis,
        rc,
        side,
        coord: rc + side * laneOff,
        d: (rnd() - 0.5) * SPAN,
        spd: 1.2 + rnd() * 2.2,
        dir: rnd() > 0.5 ? 1 : -1,
        bobf: 6 + rnd() * 5,
        ph: rnd() * Math.PI * 2,
        scale: 1.25 + rnd() * 0.4,
        laneOff,
      });
    }
    return arr;
  }, []);

  useFrame((state, dt) => {
    const s = simRef.current;
    const t = state.clock.elapsedTime;
    // gaze = how close we are to totality (0 normal, 1 deep eclipse)
    const gaze = s ? Math.min(1, Math.max(0, s.coverage)) : 0;
    const walk = 1 - gaze * 0.95;            // crowds slow to a near-stop at totality
    const lean = gaze * 0.55;                // tilt back to look up at the blood moon
    for (let i = 0; i < NPC_COUNT; i++) {
      const p = npcs[i];
      p.d += p.spd * dt * p.dir * walk;
      if (p.d > HALF) p.d -= SPAN; else if (p.d < -HALF) p.d += SPAN;
      // gather: drift inward toward the street as the eclipse deepens
      const targetCoord = p.rc + p.side * p.laneOff * (1 - gaze * 0.75);
      p.coord += (targetCoord - p.coord) * Math.min(1, dt * 1.5);
      const x = p.axis === 0 ? p.d : p.coord;
      const z = p.axis === 0 ? p.coord : p.d;
      const yb = Math.abs(Math.sin(t * p.bobf + p.ph)) * 0.06 * p.scale * walk;
      const yaw = p.axis === 0 ? (p.dir > 0 ? Math.PI / 2 : -Math.PI / 2) : (p.dir > 0 ? 0 : Math.PI);

      _dummy.rotation.set(lean, yaw, 0);      // look upward
      _dummy.scale.setScalar(p.scale);
      _dummy.position.set(x, 0.5 * p.scale + yb, z);
      _dummy.updateMatrix();
      bodyRef.current.setMatrixAt(i, _dummy.matrix);

      _dummy.position.set(x, (0.92 + lean * 0.18) * p.scale + yb, z);
      _dummy.updateMatrix();
      headRef.current.setMatrixAt(i, _dummy.matrix);

      const pulse = 0.85 + (0.3 + gaze * 0.5) * Math.sin(t * (1.4 + gaze * 4) + p.ph);
      _dummy.rotation.set(-Math.PI / 2, 0, 0);
      _dummy.scale.setScalar(p.scale * pulse);
      _dummy.position.set(x, 1.28 * p.scale + yb, z);
      _dummy.updateMatrix();
      haloRef.current.setMatrixAt(i, _dummy.matrix);
    }
    bodyRef.current.instanceMatrix.needsUpdate = true;
    headRef.current.instanceMatrix.needsUpdate = true;
    haloRef.current.instanceMatrix.needsUpdate = true;
    if (haloMat.current) {
      _tmpCol.copy(_calm).lerp(_hot, gaze);   // halos flush red at totality
      haloMat.current.color.copy(_tmpCol);
      haloMat.current.opacity = 0.4 + gaze * 0.5;
    }
  });

  return (
    <group>
      <instancedMesh ref={bodyRef} args={[undefined, undefined, NPC_COUNT]} frustumCulled={false}>
        <capsuleGeometry args={[0.16, 0.5, 3, 6]} />
        <meshLambertMaterial color={"#2b3242"} />
      </instancedMesh>
      <instancedMesh ref={headRef} args={[undefined, undefined, NPC_COUNT]} frustumCulled={false}>
        <sphereGeometry args={[0.14, 10, 10]} />
        <meshLambertMaterial color={"#c9a98b"} />
      </instancedMesh>
      <instancedMesh ref={haloRef} args={[undefined, undefined, NPC_COUNT]} frustumCulled={false}>
        <ringGeometry args={[0.2, 0.3, 18]} />
        <meshBasicMaterial ref={haloMat} color={"#38bdf8"} transparent opacity={0.7} side={THREE.DoubleSide} blending={THREE.AdditiveBlending} depthWrite={false} />
      </instancedMesh>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Traffic signals — a shared phase drives synchronized stop/go at every junction
// ---------------------------------------------------------------------------
const intersections = [];
for (let a = 0; a < roadCenters.length; a++)
  for (let b = 0; b < roadCenters.length; b++)
    intersections.push([roadCenters[a], roadCenters[b]]);

function TrafficController({ trafficRef }) {
  useFrame((state) => {
    const cycle = 10; // seconds
    const p = state.clock.elapsedTime % cycle;
    // 0–4.5 horizontal go, 4.5–5 all-stop, 5–9.5 vertical go, 9.5–10 all-stop
    trafficRef.current.h = p < 4.5;
    trafficRef.current.v = p >= 5 && p < 9.5;
  });
  return null;
}

function StopLights({ trafficRef }) {
  const poleRef = useRef();
  const hRef = useRef();
  const vRef = useRef();
  const hMat = useRef();
  const vMat = useRef();
  const green = useMemo(() => new THREE.Color("#37f07a"), []);
  const red = useMemo(() => new THREE.Color("#ff3b3b"), []);
  useEffect(() => {
    intersections.forEach((p, i) => {
      _dummy.rotation.set(0, 0, 0); _dummy.scale.set(1, 1, 1);
      // pole/housing between the two signal heads
      _dummy.position.set(p[0] + 0.7, 2.1, p[1] + 0.7); _dummy.updateMatrix();
      poleRef.current.setMatrixAt(i, _dummy.matrix);
      _dummy.position.set(p[0] + 1.4, 3.9, p[1]); _dummy.updateMatrix();
      hRef.current.setMatrixAt(i, _dummy.matrix);
      _dummy.position.set(p[0], 3.9, p[1] + 1.4); _dummy.updateMatrix();
      vRef.current.setMatrixAt(i, _dummy.matrix);
    });
    poleRef.current.instanceMatrix.needsUpdate = true;
    hRef.current.instanceMatrix.needsUpdate = true;
    vRef.current.instanceMatrix.needsUpdate = true;
  }, []);
  useFrame(() => {
    const t = trafficRef.current;
    if (hMat.current) hMat.current.color.copy(t.h ? green : red);
    if (vMat.current) vMat.current.color.copy(t.v ? green : red);
  });
  return (
    <group>
      <instancedMesh ref={poleRef} args={[undefined, undefined, intersections.length]} frustumCulled={false}>
        <cylinderGeometry args={[0.08, 0.08, 4.2, 6]} />
        <meshLambertMaterial color={"#161a22"} />
      </instancedMesh>
      <instancedMesh ref={hRef} args={[undefined, undefined, intersections.length]} frustumCulled={false}>
        <boxGeometry args={[0.42, 0.42, 0.42]} />
        <meshBasicMaterial ref={hMat} color={"#37f07a"} toneMapped={false} />
      </instancedMesh>
      <instancedMesh ref={vRef} args={[undefined, undefined, intersections.length]} frustumCulled={false}>
        <boxGeometry args={[0.42, 0.42, 0.42]} />
        <meshBasicMaterial ref={vMat} color={"#ff3b3b"} toneMapped={false} />
      </instancedMesh>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Instanced traffic — cars, buses, vans looping the street grid
// ---------------------------------------------------------------------------
function VehicleFleet({ count, size, color, emissive, seed, trafficRef }) {
  const ref = useRef();
  const lightRef = useRef();
  const data = useMemo(() => {
    const rnd = mulberry(seed);
    const arr = [];
    const lane = ROAD * 0.24;
    for (let i = 0; i < count; i++) {
      const axis = rnd() > 0.5 ? 0 : 1;
      const rc = roadCenters[Math.floor(rnd() * roadCenters.length)];
      const dir = rnd() > 0.5 ? 1 : -1;
      arr.push({
        axis,
        coord: rc - dir * lane, // drive on the correct side of the centre line
        d: (rnd() - 0.5) * SPAN,
        spd: 6 + rnd() * 7,
        dir,
      });
    }
    return arr;
  }, [count, seed]);

  useFrame((_, dt) => {
    const traffic = trafficRef.current;
    for (let i = 0; i < count; i++) {
      const v = data[i];
      // stop-and-go: obey the intersection light for the vehicle's axis
      const go = v.axis === 0 ? traffic.h : traffic.v;
      let move = true;
      if (!go) {
        let gap = Infinity;
        for (let r = 0; r < roadCenters.length; r++) {
          const g = (roadCenters[r] - v.d) * v.dir;
          if (g > 0 && g < gap) gap = g;
        }
        if (gap < 3.4) move = false; // hold at the stop line
      }
      if (move) {
        v.d += v.spd * dt * v.dir;
        if (v.d > HALF) v.d -= SPAN; else if (v.d < -HALF) v.d += SPAN;
      }
      const x = v.axis === 0 ? v.d : v.coord;
      const z = v.axis === 0 ? v.coord : v.d;
      const yaw = v.axis === 0 ? (v.dir > 0 ? Math.PI / 2 : -Math.PI / 2) : (v.dir > 0 ? 0 : Math.PI);
      _dummy.rotation.set(0, yaw, 0);
      _dummy.scale.setScalar(1);
      _dummy.position.set(x, size[1] / 2 + 0.03, z);
      _dummy.updateMatrix();
      ref.current.setMatrixAt(i, _dummy.matrix);
      const fx = x + (v.axis === 0 ? v.dir * size[2] / 2 : 0);
      const fz = z + (v.axis === 1 ? v.dir * size[2] / 2 : 0);
      _dummy.rotation.set(-Math.PI / 2, 0, 0);
      _dummy.scale.setScalar(1);
      _dummy.position.set(fx, 0.5, fz);
      _dummy.updateMatrix();
      lightRef.current.setMatrixAt(i, _dummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
    lightRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <group>
      <instancedMesh ref={ref} args={[undefined, undefined, count]} frustumCulled={false}>
        <boxGeometry args={[size[0], size[1], size[2]]} />
        <meshLambertMaterial color={color} emissive={emissive} emissiveIntensity={0.35} />
      </instancedMesh>
      <instancedMesh ref={lightRef} args={[undefined, undefined, count]} frustumCulled={false}>
        <circleGeometry args={[0.6, 10]} />
        <meshBasicMaterial color={"#fff3c8"} transparent opacity={0.55} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
      </instancedMesh>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Streetlights — instanced posts + bulbs along the road grid; only a few real lights
// ---------------------------------------------------------------------------
function StreetLights({ intensity }) {
  const postRef = useRef();
  const bulbRef = useRef();
  const spots = useMemo(() => {
    const arr = [];
    for (let a = 0; a < roadCenters.length; a++) {
      for (let b = 0; b < blockCenters.length; b++) {
        arr.push([roadCenters[a], blockCenters[b]]);
        arr.push([blockCenters[b], roadCenters[a]]);
      }
    }
    return arr;
  }, []);
  useEffect(() => {
    spots.forEach((p, i) => {
      _dummy.rotation.set(0, 0, 0); _dummy.scale.set(1, 1, 1);
      _dummy.position.set(p[0], 1.6, p[1]); _dummy.updateMatrix();
      postRef.current.setMatrixAt(i, _dummy.matrix);
      _dummy.position.set(p[0], 3.3, p[1]); _dummy.updateMatrix();
      bulbRef.current.setMatrixAt(i, _dummy.matrix);
    });
    postRef.current.instanceMatrix.needsUpdate = true;
    bulbRef.current.instanceMatrix.needsUpdate = true;
  }, [spots]);
  const lit = useMemo(() => [[14, 30], [14, 6], [14, -18]], []);
  return (
    <group>
      <instancedMesh ref={postRef} args={[undefined, undefined, spots.length]} frustumCulled={false}>
        <cylinderGeometry args={[0.06, 0.06, 3.2, 6]} />
        <meshLambertMaterial color={"#20242e"} />
      </instancedMesh>
      <instancedMesh ref={bulbRef} args={[undefined, undefined, spots.length]} frustumCulled={false}>
        <sphereGeometry args={[0.17, 8, 8]} />
        <meshBasicMaterial color={"#ffe6a8"} toneMapped={false} />
      </instancedMesh>
      {lit.map((p, i) => (
        <pointLight key={i} position={[p[0], 3.3, p[1]]} color={"#ffdca0"} intensity={intensity} distance={22} decay={2} />
      ))}
    </group>
  );
}

// ---------------------------------------------------------------------------
export default function GroundScene({ simRef, bortle }) {
  const moonLight = useRef();
  const ambient = useRef();
  const bgRef = useRef();
  const buildings = useBuildings();
  const roadTex = useMemo(() => makeRoadTexture(), []);
  const groups = useMemo(() => {
    const g = { tower: [], apartment: [], store: [], house: [] };
    let hospital = null;
    buildings.forEach((b) => { if (b.type === "hospital") hospital = b; else if (g[b.type]) g[b.type].push(b); });
    return { g, hospital };
  }, [buildings]);
  const repeats = { tower: [2, 7], apartment: [2, 5], store: [2, 2], house: [1, 2] };
  const archColor = { tower: "#151b28", apartment: "#20202b", store: "#1a2230", house: "#241f22" };

  const winIntensity = bortle >= 6 ? 0.85 : bortle >= 4 ? 0.55 : 0.32;
  const streetIntensity = bortle >= 6 ? 1.1 : bortle >= 4 ? 0.7 : 0.35;
  const trafficRef = useRef({ h: true, v: false });

  useFrame(({ scene }) => {
    const s = simRef.current;
    if (!s) return;
    const rural = bortle <= 3;
    const night = rural ? new THREE.Color(0.02, 0.022, 0.045) : new THREE.Color(0.05, 0.055, 0.09);
    const indigo = new THREE.Color(0.07, 0.035, 0.16);
    const sky = night.clone().lerp(indigo, s.coverage);
    if (!bgRef.current) { bgRef.current = new THREE.Color(); scene.background = bgRef.current; scene.fog = new THREE.Fog(bgRef.current, 60, 320); }
    bgRef.current.copy(sky).convertSRGBToLinear();

    if (moonLight.current) {
      const cool = new THREE.Color(0.85, 0.9, 1.0);
      const copper = new THREE.Color(s.danjonRgb[0], s.danjonRgb[1], s.danjonRgb[2]).multiplyScalar(3.0);
      moonLight.current.color.copy(cool.lerp(copper, s.coverage));
      moonLight.current.intensity = 1.0 + (1 - s.coverage) * 1.1 + s.coverage * (0.5 + s.danjonLum * 5);
    }
    if (ambient.current) ambient.current.intensity = 0.16 + (1 - s.coverage) * 0.14;
  });

  return (
    <>
      <ambientLight ref={ambient} intensity={0.22} />
      <hemisphereLight args={["#2a3a5c", "#0a0a12", 0.2]} />
      <Stars radius={140} depth={70} count={bortle <= 3 ? 7000 : 1500} factor={5} saturation={0} fade speed={0.3} />

      {/* Blood moon down the avenue ahead — primary light source, in the camera's view */}
      <group position={[14, 21, -70]}>
        <MoonBody simRef={simRef} radius={5} extraLight={0.3} spin={false} />
      </group>
      <directionalLight ref={moonLight} position={[14, 21, -70]} intensity={1.8} color={"#cfe0ff"} />

      {/* ground */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[SPAN * 1.6, SPAN * 1.6]} />
        <meshLambertMaterial color={"#0e1017"} />
      </mesh>

      <Roads roadTex={roadTex} />
      {Object.keys(groups.g).map((type) =>
        groups.g[type].length ? (
          <InstancedBuildings
            key={type}
            items={groups.g[type]}
            color={archColor[type]}
            tex={groups.g[type][0].tex}
            repeat={repeats[type]}
            winIntensity={winIntensity}
          />
        ) : null
      )}
      {groups.hospital && <Hospital item={groups.hospital} winIntensity={winIntensity} />}
      <StreetLights intensity={streetIntensity} />
      <TrafficController trafficRef={trafficRef} />
      <StopLights trafficRef={trafficRef} />

      {/* Traffic */}
      <VehicleFleet count={26} size={[1.9, 0.9, 4]} color={"#c23b3b"} emissive={"#ff3020"} seed={101} trafficRef={trafficRef} />
      <VehicleFleet count={8} size={[2.4, 2.6, 8.5]} color={"#e0a52f"} emissive={"#ffb020"} seed={202} trafficRef={trafficRef} />
      <VehicleFleet count={12} size={[2.2, 2.1, 5.2]} color={"#c6ccd6"} emissive={"#ffd060"} seed={303} trafficRef={trafficRef} />

      {/* 500 instanced pedestrians with neural halos */}
      <Crowd simRef={simRef} />
    </>
  );
}
